/**
 * BasicInfoTab — Device details, status/sensor display, and event location map.
 *
 * Conditionally renders based on device type:
 *   - Inovonics: Device Details + Status & Sensors (temp, humidity, battery)
 *   - Palcare:   Device Details (+ Line Profile field) + Event Location Map
 */

import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {LayersControl, MapContainer, Marker, Popup} from 'react-leaflet';
import ReactLeafletGoogleLayer from 'react-leaflet-google-layer';
import { convertIsoToLocal } from '../../../../libs.jsx';
import { GOOGLE_MAPS_API_KEY as GOOGLE_API_KEY } from '../../../../constants/mapsConfig.js';

const BasicInfoTab = ({
  basicData, deviceName, description, serialNum, modelName,
  deviceTypeName, deviceCategory, behaviorText,
  isAreaDevice, areaId, areaName, residentId, residentName,
  lastRepeater, lastAlarm, lastActive,
  deviceTypeId, currentTemp, currentHumidity, currentBattery,
  deviceImg, isActive, loading, editRights, onEdit, onUnassign,
  /* Palcare-specific props */
  isPalcare, dspProfileName, eventData, campusLat, campusLong, tzOffset,
}) => {

  // Battery display helper (Inovonics only)
  const getBatteryInfo = (voltage) => {
    if (!voltage) return null;
    const v = parseFloat(voltage);
    if (v >= 2.8) return { level: 'full', label: `Full ${v}V`, icon: '🔋' };
    if (v === 2.7) return { level: 'good', label: `Good ${v}V`, icon: '🔋' };
    if (v >= 2.5) return { level: 'low', label: `Low ${v}V`, icon: '🪫' };
    return { level: 'critical', label: `Critical ${v}V`, icon: '⚠' };
  };

  const battery = !isPalcare ? getBatteryInfo(currentBattery) : null;

  // Event markers for Palcare map
  const eventMarkers = useMemo(() => {
    if (!isPalcare || !eventData?.length) return [];
    return eventData.filter(
      (evt) => evt.latitude && evt.longitude
               && evt.latitude !== 0 && evt.longitude !== 0
    );
  }, [isPalcare, eventData]);

  // Map center: first event with coords, else campus center
  const mapCenter = useMemo(() => {
    if (eventMarkers.length > 0) {
      return [eventMarkers[0].latitude, eventMarkers[0].longitude];
    }
    return [campusLat || 0, campusLong || 0];
  }, [eventMarkers, campusLat, campusLong]);

  return (
    <>
      {/* Device Details */}
      <div className="pp-section">
        <div className="pp-section__header">
          <h3 className="pp-section__title">Device Details</h3>
          <div className="pp-section__actions">
            {editRights && (
              <>
                <button className="dp-btn dp-btn--secondary dp-btn--sm" onClick={onEdit}>
                  ✎ Edit
                </button>
                {!!isActive && (
                  <button className="dp-btn dp-btn--danger dp-btn--sm" onClick={onUnassign}>
                    Unassign
                  </button>
                )}
              </>
            )}
          </div>
        </div>
        <div className="pp-section__body">
          <div style={{ display: 'flex', gap: 'var(--space-5)' }}>
            {/* Device image */}
            {deviceImg && (
              <div className="ip-device-image">
                <img
                  className="ip-device-image__img"
                  src={`/static/image/${deviceImg}`}
                  alt={deviceName}
                />
              </div>
            )}

            {/* Field grid */}
            <div className="pp-field-grid" style={{ flex: 1 }}>
              <div className="pp-field">
                <span className="pp-field__label">Device Name</span>
                <span className="pp-field__value">{deviceName || '—'}</span>
              </div>
              <div className="pp-field">
                <span className="pp-field__label">Description</span>
                <span className="pp-field__value">{description || '—'}</span>
              </div>
              <div className="pp-field">
                <span className="pp-field__label">Category</span>
                <span className="pp-field__value">{deviceCategory || '—'}</span>
              </div>
              <div className="pp-field">
                <span className="pp-field__label">Device Type</span>
                <span className="pp-field__value">{deviceTypeName || '—'}</span>
              </div>
              {/* Behavior (Inovonics) / Line Profile (Palcare) */}
              {isPalcare ? (
                <div className="pp-field">
                  <span className="pp-field__label">Line Profile</span>
                  <span className="pp-field__value">{dspProfileName || 'No Profile'}</span>
                </div>
              ) : (
                <div className="pp-field">
                  <span className="pp-field__label">Behavior</span>
                  <span className="pp-field__value">{behaviorText || 'Standard'}</span>
                </div>
              )}
              <div className="pp-field">
                <span className="pp-field__label">Serial Number</span>
                <span className="pp-field__value pp-field__value--mono">{serialNum || '—'}</span>
              </div>
              <div className="pp-field">
                <span className="pp-field__label">Model Number</span>
                <span className="pp-field__value">{modelName || '—'}</span>
              </div>
              {!isAreaDevice && (
                <div className="pp-field">
                  <span className="pp-field__label">Room</span>
                  <span className="pp-field__value">
                    {areaId ? (
                      <Link to={`/community/areas/${areaId}`} className="pp-field__value--link">
                        {areaName || '—'}
                      </Link>
                    ) : '—'}
                  </span>
                </div>
              )}
              <div className="pp-field">
                <span className="pp-field__label">Assigned To</span>
                <span className="pp-field__value">
                  {isAreaDevice ? (
                    areaId ? (
                      <Link to={`/community/areas/${areaId}`} className="pp-field__value--link">
                        {areaName || '—'}
                      </Link>
                    ) : '—'
                  ) : (
                    residentId ? (
                      <Link to={`/community/residents/${residentId}`} className="pp-field__value--link">
                        {residentName || '—'}
                      </Link>
                    ) : '—'
                  )}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Inovonics: Status & Sensors                                        */}
      {/* ------------------------------------------------------------------ */}
      {!isPalcare && (
        <div className="pp-section">
          <div className="pp-section__header">
            <h3 className="pp-section__title">Status & Sensors</h3>
          </div>
          <div className="pp-section__body">
            <div className="pp-field-grid pp-field-grid--3col">
              <div className="pp-field">
                <span className="pp-field__label">Last Repeater</span>
                <span className="pp-field__value">{lastRepeater || 'No Record'}</span>
              </div>
              <div className="pp-field">
                <span className="pp-field__label">Last Alarmed</span>
                <span className="pp-field__value">
                  {lastAlarm ? convertIsoToLocal(lastAlarm) : 'No Record'}
                </span>
              </div>
              <div className="pp-field">
                <span className="pp-field__label">Last Active</span>
                <span className="pp-field__value">
                  {lastActive ? convertIsoToLocal(lastActive) : 'No Record'}
                </span>
              </div>

              {/* Temperature (device types 1, 28) */}
              {[1, 28].includes(deviceTypeId) && (
                <div className="pp-field">
                  <span className="pp-field__label">Last Temperature</span>
                  <span className="pp-field__value">
                    {currentTemp ? (
                      <span className="ip-sensor-value">
                        {currentTemp}<span className="ip-sensor-value__unit">{'\u00B0'}F</span>
                      </span>
                    ) : '—'}
                  </span>
                </div>
              )}

              {/* Humidity (device type 28) */}
              {[28].includes(deviceTypeId) && (
                <div className="pp-field">
                  <span className="pp-field__label">Last Humidity</span>
                  <span className="pp-field__value">
                    {currentHumidity ? (
                      <span className="ip-sensor-value">
                        {currentHumidity}<span className="ip-sensor-value__unit">%</span>
                      </span>
                    ) : '—'}
                  </span>
                </div>
              )}

              {/* Battery (device type 1) */}
              {[1].includes(deviceTypeId) && battery && (
                <div className="pp-field">
                  <span className="pp-field__label">Battery</span>
                  <span className="pp-field__value">
                    <span className={`ip-battery ip-battery--${battery.level}`} title={battery.label}>
                      <span className="ip-battery__icon">{battery.icon}</span>
                      <span className="ip-battery__label">{battery.label}</span>
                    </span>
                  </span>
                </div>
              )}

              <div className="pp-field">
                <span className="pp-field__label">Status</span>
                <span className="pp-field__value">
                  <span className={`pp-profile-banner__status pp-profile-banner__status--${isActive ? 'online' : 'offline'}`}>
                    <span className="pp-profile-banner__status-dot" />
                    {isActive ? 'Active' : 'Unassigned'}
                  </span>
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Palcare: Event Location Map                                        */}
      {/* ------------------------------------------------------------------ */}
      {isPalcare && (
        <div className="pp-section">
          <div className="pp-section__header">
            <h3 className="pp-section__title">Event Locations</h3>
            <div className="pp-section__actions">
              <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>
                {eventMarkers.length > 0
                  ? `${eventMarkers.length} event${eventMarkers.length !== 1 ? 's' : ''} with location data`
                  : 'No events with location data'}
              </span>
            </div>
          </div>
          <div className="pp-section__body pp-section__body--flush">
            <div className="ip-map-container">
              <MapContainer
                zoom={19}
                scrollWheelZoom={true}
                center={mapCenter}
                style={{ height: '100%', width: '100%' }}
              >
                <LayersControl position="topright">
                  <LayersControl.BaseLayer checked name="Roadmap">
                    <ReactLeafletGoogleLayer apiKey={GOOGLE_API_KEY} type="roadmap" />
                  </LayersControl.BaseLayer>
                  <LayersControl.BaseLayer name="Satellite">
                    <ReactLeafletGoogleLayer apiKey={GOOGLE_API_KEY} type="satellite" />
                  </LayersControl.BaseLayer>
                  <LayersControl.BaseLayer name="Terrain">
                    <ReactLeafletGoogleLayer apiKey={GOOGLE_API_KEY} type="terrain" />
                  </LayersControl.BaseLayer>
                  <LayersControl.BaseLayer name="Hybrid">
                    <ReactLeafletGoogleLayer apiKey={GOOGLE_API_KEY} type="hybrid" />
                  </LayersControl.BaseLayer>
                </LayersControl>
                {eventMarkers.map((evt, idx) => (
                  <Marker
                    key={`event-marker-${evt.event_id || idx}`}
                    position={[evt.latitude, evt.longitude]}
                  >
                    <Popup>
                      <div style={{ fontSize: '0.85rem', lineHeight: 1.5 }}>
                        <strong>Event Time:</strong> {convertIsoToLocal(evt.event_datetime_local)}<br />
                        <strong>Event Clear:</strong> {evt.ack_datetime_local ? convertIsoToLocal(evt.ack_datetime_local) : '—'}<br />
                        <strong>Response:</strong> {evt.response_time ?? '—'} Minutes<br />
                        <strong>Reason:</strong> {evt.reason_code_description || 'No Reason'}
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BasicInfoTab;
